/**
 * TOP FAILING TAB (release scope only)
 *
 * Shows the 10 scenarios in the release with the most failed executions.
 * Pairs a horizontal bar chart with a detail table so users can both see the
 * shape and drill into specifics (fail rate, last execution).
 */

import React, { useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import Spinner from '@atlaskit/spinner'
import { token } from '@atlaskit/tokens'

import TopFailingChart from '../charts/TopFailingChart'
import {
  fetchTopFailingAsync,
  selectTopFailing,
  selectReportLoading,
  selectReportErrors
} from '../../../store/slices/reportsSlice'

const formatDate = (value) => {
  if (!value) return '—'
  return new Date(value).toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  })
}

const thStyle = {
  padding: '10px 12px',
  textAlign: 'left',
  fontWeight: 500,
  color: token('color.text.subtle', '#6B778C'),
  borderBottom: `1px solid ${token('color.border', '#DFE1E6')}`,
  fontSize: '12px',
  textTransform: 'uppercase',
  letterSpacing: '0.3px'
}

const tdStyle = {
  padding: '10px 12px',
  color: token('color.text', '#172B4D'),
  fontSize: '13px',
  borderBottom: `1px solid ${token('color.border', '#DFE1E6')}`
}

const rowBg = (index) =>
  index % 2 === 0
    ? token('elevation.surface', '#FFFFFF')
    : token('color.background.neutral.subtle', '#FAFBFC')

const TopFailingTab = ({ releaseId }) => {
  const dispatch = useDispatch()
  const data = useSelector(selectTopFailing)
  const loading = useSelector(selectReportLoading)
  const errors = useSelector(selectReportErrors)

  useEffect(() => {
    if (releaseId) {
      dispatch(fetchTopFailingAsync({ releaseId }))
    }
  }, [dispatch, releaseId])

  if (loading.topFailing) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: '60px 0' }}>
        <Spinner size="large" />
      </div>
    )
  }

  if (errors.topFailing) {
    return (
      <div style={{ padding: '40px', textAlign: 'center', color: token('color.text.danger', '#DE350B') }}>
        <p>Failed to load top-failing scenarios</p>
        <p style={{ fontSize: '12px', color: token('color.text.subtle', '#6B778C') }}>{errors.topFailing}</p>
      </div>
    )
  }

  if (!data) return null

  const scenarios = data.scenarios || []

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
      <section>
        <div
          style={{
            fontSize: '14px',
            fontWeight: 600,
            color: token('color.text', '#172B4D'),
            marginBottom: '8px'
          }}
        >
          Scenarios with most failures
        </div>
        <TopFailingChart scenarios={scenarios} />
      </section>

      {scenarios.length > 0 && (
        <section>
          <div
            style={{
              border: `1px solid ${token('color.border', '#DFE1E6')}`,
              borderRadius: '4px',
              overflow: 'hidden'
            }}
          >
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ backgroundColor: token('color.background.neutral', '#F4F5F7') }}>
                  <th style={thStyle}>Scenario</th>
                  <th style={{ ...thStyle, textAlign: 'right' }}>Fails</th>
                  <th style={{ ...thStyle, textAlign: 'right' }}>Total</th>
                  <th style={{ ...thStyle, textAlign: 'right' }}>Fail rate</th>
                  <th style={thStyle}>Last execution</th>
                </tr>
              </thead>
              <tbody>
                {scenarios.map((s, idx) => (
                  <tr key={s.scenarioId} style={{ backgroundColor: rowBg(idx) }}>
                    <td style={tdStyle}>
                      {s.scenarioKey && (
                        <span
                          style={{
                            display: 'inline-block',
                            marginRight: '8px',
                            padding: '1px 6px',
                            fontSize: '11px',
                            fontWeight: 600,
                            borderRadius: '3px',
                            backgroundColor: token('color.background.neutral', '#F4F5F7'),
                            color: token('color.text.subtle', '#6B778C')
                          }}
                        >
                          {s.scenarioKey}
                        </span>
                      )}
                      {s.scenarioName}
                    </td>
                    <td style={{ ...tdStyle, textAlign: 'right', fontWeight: 600, color: token('color.text.danger', '#DE350B') }}>
                      {s.failCount}
                    </td>
                    <td style={{ ...tdStyle, textAlign: 'right', color: token('color.text.subtle', '#6B778C') }}>
                      {s.totalExecutions}
                    </td>
                    <td style={{ ...tdStyle, textAlign: 'right', fontWeight: 600 }}>
                      {Number(s.failRate || 0).toFixed(1)}%
                    </td>
                    <td style={{ ...tdStyle, color: token('color.text.subtle', '#6B778C') }}>
                      {formatDate(s.lastExecutionDate)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}
    </div>
  )
}

export default TopFailingTab
